import { CallFlowActionType } from "@/model/interface/CallFlowActionType";
import { ActionNodeData, CallFlowPrompt } from "@/model/interface/CallFlow";
import { DEFAULT_VOICE } from "@/util/ttsVoices";
import { isUsE164 } from "@/util/validation";

export const EDGE_NEXT = "next";
export const EDGE_NO_ANSWER = "noAnswer";
export const EDGE_NO_INPUT = "noInput";
export const EDGE_KEY_PREFIX = "key:";
export const EDGE_OPEN = "open";
export const EDGE_CLOSED = "closed";
export const EDGE_NO_OWNER = "noOwner";

export const WEEK_DAYS = [
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
  "SATURDAY",
  "SUNDAY",
] as const;

export const NODE_EDGES: Record<CallFlowActionType, string[]> = {
  [CallFlowActionType.CALL_USER]: [EDGE_NO_ANSWER],
  [CallFlowActionType.CALL_NUMBER]: [EDGE_NO_ANSWER],
  [CallFlowActionType.SAY_MESSAGE]: [EDGE_NEXT],
  [CallFlowActionType.PLAY_AUDIO]: [EDGE_NEXT],
  [CallFlowActionType.VOICEMAIL]: [],
  [CallFlowActionType.AI_AGENT]: [],
  [CallFlowActionType.MENU]: [EDGE_NO_INPUT],
  [CallFlowActionType.BUSINESS_HOURS]: [EDGE_OPEN, EDGE_CLOSED],
  [CallFlowActionType.RING_GROUP]: [EDGE_NO_ANSWER],
  [CallFlowActionType.ROUND_ROBIN]: [EDGE_NO_ANSWER],
  [CallFlowActionType.ROUTE_TO_OWNER]: [EDGE_NO_ANSWER, EDGE_NO_OWNER],
  [CallFlowActionType.HANGUP]: [],
  [CallFlowActionType.DNC_OPT_OUT]: [],
};

export const EDGE_LABELS: Record<string, string> = {
  [EDGE_NEXT]: "Next",
  [EDGE_NO_ANSWER]: "No answer",
  [EDGE_NO_INPUT]: "No input",
  [EDGE_OPEN]: "Open",
  [EDGE_CLOSED]: "Closed",
  [EDGE_NO_OWNER]: "No owner",
};

export function menuKeysIn(data?: Record<string, unknown>): string[] {
  const keys = data?.menuKeys;
  if (!Array.isArray(keys)) {
    return [];
  }
  return keys.map((key) => String(key)).filter((key) => key.length > 0);
}

export function edgeKeysFor(actionType: CallFlowActionType, data?: Record<string, unknown>): string[] {
  if (actionType !== CallFlowActionType.MENU) {
    return NODE_EDGES[actionType] ?? [];
  }
  return [...menuKeysIn(data).map((key) => `${EDGE_KEY_PREFIX}${key}`), EDGE_NO_INPUT];
}

export function edgeLabelFor(edgeKey: string): string {
  if (edgeKey.startsWith(EDGE_KEY_PREFIX)) {
    return `Press ${edgeKey.slice(EDGE_KEY_PREFIX.length)}`;
  }
  return EDGE_LABELS[edgeKey] ?? edgeKey;
}

export const NODE_LABELS: Record<CallFlowActionType, string> = {
  [CallFlowActionType.CALL_USER]: "Call User",
  [CallFlowActionType.CALL_NUMBER]: "Call Number",
  [CallFlowActionType.SAY_MESSAGE]: "Say Message",
  [CallFlowActionType.PLAY_AUDIO]: "Play Audio",
  [CallFlowActionType.VOICEMAIL]: "Voicemail",
  [CallFlowActionType.AI_AGENT]: "AI Agent",
  [CallFlowActionType.MENU]: "Menu",
  [CallFlowActionType.BUSINESS_HOURS]: "Business Hours",
  [CallFlowActionType.RING_GROUP]: "Ring Group",
  [CallFlowActionType.ROUND_ROBIN]: "Round Robin",
  [CallFlowActionType.ROUTE_TO_OWNER]: "Route To Owner",
  [CallFlowActionType.HANGUP]: "Hangup",
  [CallFlowActionType.DNC_OPT_OUT]: "Do Not Call Opt-Out",
};

export function businessHoursDaysIn(data?: Record<string, unknown>): string[] {
  const schedule = data?.businessHours as { days?: Record<string, unknown[]> } | undefined;
  if (!schedule?.days) {
    return [];
  }
  return WEEK_DAYS.filter((day) => (schedule.days?.[day] ?? []).length > 0);
}

export function isTerminalNodeType(actionType: CallFlowActionType): boolean {
  return (NODE_EDGES[actionType] ?? []).length === 0;
}

export const DEFAULT_VOICEMAIL_GREETING = "Please leave a message after the beep.";
export const DEFAULT_WHISPER_TEXT = "Call from %contactName% on %numberName%";

export function withActionDefaults(
  data: Partial<ActionNodeData> & { actionType: CallFlowActionType },
): ActionNodeData {
  return {
    ...data,
    actionType: data.actionType,
    label: data.label || NODE_LABELS[data.actionType],
    message: data.message ?? "",
    targetPhoneNumber: data.targetPhoneNumber ?? "",
    targetMembershipId: data.targetMembershipId ?? "",
    targetUserName: data.targetUserName ?? "",
    voicemailGreeting: data.voicemailGreeting || DEFAULT_VOICEMAIL_GREETING,
    timeoutSeconds: data.timeoutSeconds || 30,
    voice: data.voice || DEFAULT_VOICE,
    language: data.language || "en-US",
    loop: data.loop || 1,
    pauseLength: data.pauseLength ?? 0,
    aiAgentId: data.aiAgentId ?? "",
    callScope: data.callScope ?? "",
    prompt: data.prompt ?? { mode: "SAY", text: data.message || data.voicemailGreeting || "" },
    invalidPrompt: data.invalidPrompt ?? { mode: "SAY", text: "" },
    businessHours: data.businessHours ?? { days: {}, holidays: [] },
    ringMembers: data.ringMembers ?? [],
    ringStrategy: data.ringStrategy || "SIMULTANEOUS",
    ringSeconds: data.ringSeconds ?? 20,
    whisper: data.whisper ?? { mode: "SAY", text: DEFAULT_WHISPER_TEXT },
    forwardCallerIdMode: data.forwardCallerIdMode || "RECEIVING",
    forwardRecord: data.forwardRecord ?? false,
    ringRounds: data.ringRounds ?? 1,
    ringStickyAgent: data.ringStickyAgent ?? true,
    menuKeys: data.menuKeys ?? [],
    menuKeyLabels: data.menuKeyLabels ?? {},
    menuRepeatCount: data.menuRepeatCount ?? 1,
    menuReturnKey: data.menuReturnKey || "",
    voicemailMaxLengthSeconds: data.voicemailMaxLengthSeconds ?? 300,
    voicemailTranscribe: data.voicemailTranscribe ?? false,
    voicemailGreetingSource: data.voicemailGreetingSource || "STEP",
  };
}

function hasPrompt(data: Record<string, unknown>): boolean {
  const prompt = data.prompt as CallFlowPrompt | undefined;
  if (prompt?.mode === "PLAY") {
    return Boolean(prompt.audioClipId);
  }
  return Boolean(prompt?.text || data.message);
}

export function missingRequiredFields(actionType: CallFlowActionType, data: Record<string, unknown>): string[] {
  const missing: string[] = [];
  if (actionType === CallFlowActionType.CALL_USER && !data.targetMembershipId) {
    missing.push("user");
  }
  if (actionType === CallFlowActionType.CALL_NUMBER && !data.targetPhoneNumber) {
    missing.push("phone number");
  }
  if (actionType === CallFlowActionType.SAY_MESSAGE && !hasPrompt(data)) {
    missing.push("message");
  }
  if (actionType === CallFlowActionType.PLAY_AUDIO && !hasPrompt(data)) {
    missing.push("audio clip");
  }
  if (actionType === CallFlowActionType.AI_AGENT && !data.aiAgentId) {
    missing.push("AI agent");
  }
  const transferNumber = data.transferNumber;
  if (actionType === CallFlowActionType.AI_AGENT && typeof transferNumber === "string" && transferNumber && !isUsE164(transferNumber)) {
    missing.push("valid US transfer number");
  }
  if (actionType === CallFlowActionType.BUSINESS_HOURS && businessHoursDaysIn(data).length === 0) {
    missing.push("opening hours");
  }
  const isRingList =
    actionType === CallFlowActionType.RING_GROUP || actionType === CallFlowActionType.ROUND_ROBIN;
  if (isRingList && !data.ringTeamId && !(data.ringMembers as string[] | undefined)?.length) {
    missing.push("a team or members");
  }
  if (actionType === CallFlowActionType.MENU) {
    if (!hasPrompt(data)) {
      missing.push("prompt");
    }
    if (menuKeysIn(data).length === 0) {
      missing.push("key options");
    }
  }
  return missing;
}
